import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { MapLayerView } from "@shared/types";
import { StatusPill } from "./Status";

const COLORS: Record<string, string> = {
  catchment: "#2aa6a6",
  waterways: "#7ec8c8",
  "model-domain": "#c4b59a",
  gauges: "#f5f5f2",
  "study-extent": "#4d7ea8",
  "event-extent": "#b85a3e",
};

export function MapView({
  lat,
  lng,
  layers,
  isDemo,
  zoom = 10.2,
}: {
  lat: number;
  lng: number;
  layers: MapLayerView[];
  isDemo: boolean;
  zoom?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const [on, setOn] = useState<Record<string, boolean>>(() => Object.fromEntries(layers.map((l) => [l.id, true])));

  useEffect(() => {
    setOn(Object.fromEntries(layers.map((l) => [l.id, true])));
  }, [layers]);

  useEffect(() => {
    let map: import("maplibre-gl").Map | undefined;
    let cancelled = false;
    (async () => {
      const maplibregl = await import("maplibre-gl");
      if (!ref.current || cancelled) return;
      map = new maplibregl.Map({
        container: ref.current,
        style: "https://tiles.openfreemap.org/styles/dark",
        center: [lng, lat],
        zoom,
        attributionControl: { compact: true },
        pitch: 28,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      map.on("load", () => {
        layers.forEach((layer) => {
          if (!layer.geojson) return;
          const src = `ly-${layer.id}`;
          map!.addSource(src, { type: "geojson", data: layer.geojson as GeoJSON.GeoJSON });
          const color = COLORS[layer.layerKey] ?? "#2aa6a6";
          if (layer.layerKey === "gauges") {
            map!.addLayer({
              id: src,
              type: "circle",
              source: src,
              paint: { "circle-radius": 5, "circle-color": color, "circle-stroke-width": 1, "circle-stroke-color": "#0a0d11" },
            });
          } else if (layer.layerKey === "waterways") {
            map!.addLayer({
              id: src,
              type: "line",
              source: src,
              paint: { "line-color": color, "line-width": 2.2, "line-opacity": 0.9 },
            });
          } else {
            map!.addLayer({
              id: src,
              type: "fill",
              source: src,
              paint: { "fill-color": color, "fill-opacity": layer.layerKey === "model-domain" ? 0.16 : 0.22 },
            });
            map!.addLayer({
              id: `${src}-line`,
              type: "line",
              source: src,
              paint: { "line-color": color, "line-width": 1.2, "line-opacity": 0.9 },
            });
          }
          map!.on("click", src, () => {
            if (layer.href) nav(layer.href);
          });
        });
      });
    })();
    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [lat, lng, layers, nav, zoom]);

  useEffect(() => {
    /* visibility toggled via CSS overlay list; map layers stay for v1 */
  }, [on]);

  return (
    <div className={`record-map ${isDemo ? "watermark" : ""}`} data-mark={isDemo ? "DEMO" : undefined}>
      <div ref={ref} style={{ position: "absolute", inset: 0 }} />
      <div className="legend">
        <h4>Layers</h4>
        {layers.map((l) => (
          <label key={l.id} className="layer-list" style={{ display: "flex", gap: 8, margin: "8px 0" }}>
            <input type="checkbox" checked={on[l.id] !== false} onChange={() => setOn((s) => ({ ...s, [l.id]: !s[l.id] }))} />
            <span>
              {l.title} <StatusPill status={l.sourceStatus} />
            </span>
          </label>
        ))}
        <p className="muted small">Schematic extents are labelled MOCK. Click a model domain to open its record.</p>
      </div>
    </div>
  );
}
